/**
 * What is worth a toast (beta; `views/live-toast.ts`), DOM-free. Two events only, the two a push is sent for
 * (`raid-in`, `raid-done`): an army launched at the learner, and a raid they sent coming back. A friend joining or
 * leaving is already on Friends and stays silent.
 *
 * An event is something that was not in the learner's last read of `GET /api/raids`. The first read is only the
 * baseline, so nothing that was already waiting is announced, and a read that finds the toast unwelcome (below)
 * still moves the baseline: an army that came while a card was open is never announced afterwards, home's line shows it.
 */
import type { RaidOverview } from './views/raid/raid-api';

/** The raid ids the learner's last read held. */
export interface Seen { incoming: Set<string>; reports: Set<string> }

export type LiveEvent =
  | { kind: 'army'; id: string; name: string; weapons: number }
  | { kind: 'report'; id: string; name: string };

export const seenOf = (o: Pick<RaidOverview, 'incoming' | 'reports'>): Seen => ({
  incoming: new Set(o.incoming.map((r) => r.id)),
  reports: new Set(o.reports.map((r) => r.id)),
});

/**
 * Armies first (they have a clock), then reports of raids the learner sent and has not read. A defender's own
 * report is never one: they were there, or it played itself and home's line says so. No baseline, no events.
 */
export function newEvents(prev: Seen | undefined, o: Pick<RaidOverview, 'incoming' | 'reports'>): LiveEvent[] {
  if (!prev) return [];
  const armies = o.incoming
    .filter((r) => !prev.incoming.has(r.id))
    .map((r): LiveEvent => ({ kind: 'army', id: r.id, name: r.from.name, weapons: r.weapons }));
  const reports = o.reports
    .filter((r) => r.side === 'attacker' && r.unread && !prev.reports.has(r.id))
    .map((r): LiveEvent => ({ kind: 'report', id: r.id, name: r.other.name }));
  return [...armies, ...reports];
}

/** Overlays on `<html>` (`home-overlays.ts`) where a learner is writing, recording or answering a card: never a toast. */
const BUSY = ['capture-open', 'lantern-open', 'tend-open', 'report-open', 'reminders-open'];
/** Overlays that hide home's raid line, so a toast is the only thing that says it. */
const COVERED = ['moments-open', 'legend-open', 'town-sheet-open', 'invite-open', 'moment-open'];

/**
 * Only where the news would otherwise be hidden: home behind a sheet, or a moment's screen (a panel over home).
 * Plain home shows the raid line, Friends and the raid's own screens show the raids themselves, and everything else
 * (the round, a lesson, a landing, practice, an exam, recording, the first night, signed-out pages) is somebody
 * concentrating.
 */
export function toastAllowed(route: string, open: readonly string[]): boolean {
  if (route !== 'home' && route !== 'moment') return false;
  if (open.some((c) => BUSY.includes(c))) return false;
  return open.some((c) => COVERED.includes(c));
}
